/**
 * Send to the arche the person came from — client-side entity executor
 * (ADR-037 D8): "Use in <arche>" in FiasWorks Studio.
 *
 * Runs in the HOST page, never in the plugin iframe. A plugin offers one of
 * its own files; the host — which alone knows where the person came from (a
 * Fias Forge, an Arche Builder arche) — draws a sheet naming the file and the
 * destination, and the person's Send moves it through a person-session route.
 * The plugin names only the file and a suggested name; it learns whether the
 * file was sent.
 *
 * Host services arrive through `configureSendToOpenerHost` at registration
 * (`client/src/lib/register-client-entities.ts`), because the executor context
 * carries only `{ userId, archeId, holderId }`.
 *
 * Expected failures are RESULTS, not throws, so a typed code reaches the SDK.
 */

import type { ClientEntityDefinition, ClientEntityInvocationContext } from './types';

export const SEND_TO_OPENER_ENTITY_ID = 'ent_intg_send_to_opener_00000000';

export type SendToOpenerErrorCode =
  | 'INVALID_INPUT'
  | 'SEND_UNAVAILABLE'
  | 'NO_OPENER'
  | 'SHEET_BUSY';

export type SendToOpenerResult =
  | { sent: true; name: string }
  | { canceled: true }
  | { error: { code: SendToOpenerErrorCode; message: string } };

/** What the host page provides: the sheet, drawn for one arche. */
export interface SendToOpenerHost {
  showSendToOpener(request: {
    archeId: string;
    holderId: string | null;
    fileId: string;
    name: string | null;
  }): Promise<SendToOpenerResult>;
}

let host: SendToOpenerHost | null = null;

/** Called once by the host page at registration. */
export function configureSendToOpenerHost(next: SendToOpenerHost | null): void {
  host = next;
}

/** A Vault document id, as the platform mints them. */
const FILE_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;
const NAME_MAX = 120;

const INVALID = (message: string): SendToOpenerResult => ({
  error: { code: 'INVALID_INPUT', message },
});

export async function sendToOpenerExecutor(
  input: unknown,
  context: ClientEntityInvocationContext,
): Promise<SendToOpenerResult> {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return INVALID('send takes { fileId, name? }');
  }
  const { fileId, name, ...rest } = input as Record<string, unknown>;
  if (Object.keys(rest).length > 0) return INVALID('send takes only { fileId, name? }');
  if (typeof fileId !== 'string' || !FILE_ID_RE.test(fileId)) {
    return INVALID('fileId is the id of a Vault file this arche made');
  }
  if (name !== undefined && (typeof name !== 'string' || !name.trim() || name.length > NAME_MAX)) {
    return INVALID(`name is a file name of at most ${NAME_MAX} characters`);
  }
  if (!host) {
    return {
      error: {
        code: 'SEND_UNAVAILABLE',
        message: 'Sending to another arche is not available here',
      },
    };
  }
  return host.showSendToOpener({
    archeId: context.archeId,
    holderId: context.holderId ?? null,
    fileId,
    name: typeof name === 'string' ? name.trim() : null,
  });
}

export const sendToOpenerDefinition = {
  entityId: SEND_TO_OPENER_ENTITY_ID,
  rateLimitPerMinute: 10,
  pluginPermission: 'opener:send',
  executor: sendToOpenerExecutor,
} as const satisfies ClientEntityDefinition<unknown, SendToOpenerResult>;
